import { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { emailService, SendQuoteEmailParams } from '../../../services/emailService';

interface SendQuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote: {
    id: string;
    referenceCode: string;
    customerName: string;
    customerEmail: string;
    companyName: string;
    projectName: string;
    totalExVAT: number;
    validUntil: string;
  } | null;
}

const SendQuoteModal = ({ isOpen, onClose, quote }: SendQuoteModalProps) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize template when modal opens or quote changes
  useEffect(() => {
    if (isOpen && quote) {
      // Set default subject
      setSubject(`Your Quote - ${quote.referenceCode}`);
      
      // Get default template with variables
      const variables = {
        customerName: quote.customerName,
        companyName: quote.companyName,
        quoteReference: quote.referenceCode,
        projectName: quote.projectName,
        quoteTotal: `€${quote.totalExVAT.toFixed(2)}`,
        validUntilDate: new Date(quote.validUntil).toLocaleDateString('en-GB'),
        quoteLink: `${window.location.origin}/customer-quote-review-portal/${quote.id}`,
      };
      
      const defaultTemplate = emailService.getDefaultTemplate(variables);
      setBody(defaultTemplate);
      setError(null);
      setIsPreview(false);
    }
  }, [isOpen, quote]);

  const replaceVariables = (text: string): string => {
    if (!quote) return text;
    
    const variables = {
      '{CUSTOMER_NAME}': quote.customerName,
      '{COMPANY_NAME}': quote.companyName,
      '{QUOTE_REFERENCE}': quote.referenceCode,
      '{PROJECT_NAME}': quote.projectName,
      '{QUOTE_TOTAL}': `€${quote.totalExVAT.toFixed(2)}`,
      '{VALID_UNTIL_DATE}': new Date(quote.validUntil).toLocaleDateString('en-GB'),
      '{QUOTE_LINK}': `${window.location.origin}/customer-quote-review-portal/${quote.id}`,
    };
    
    let result = text;
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(key, 'g'), value);
    });
    
    return result;
  };

  const handleSend = async () => {
    if (!quote) return;
    
    // Validation
    if (!subject.trim()) {
      setError('Subject cannot be empty');
      return;
    }
    
    if (!body.trim()) {
      setError('Email body cannot be empty');
      return;
    }
    
    setIsSending(true);
    setError(null);
    
    try {
      const emailParams: SendQuoteEmailParams = {
        to: quote.customerEmail,
        subject: subject,
        body: body,
        quoteId: quote.id,
        variables: {
          customerName: quote.customerName,
          companyName: quote.companyName,
          quoteReference: quote.referenceCode,
          projectName: quote.projectName,
          quoteTotal: `€${quote.totalExVAT.toFixed(2)}`,
          validUntilDate: new Date(quote.validUntil).toLocaleDateString('en-GB'),
          quoteLink: `${window.location.origin}/customer-quote-review-portal/${quote.id}`,
        },
      };
      
      let result = await emailService.sendQuoteEmail(emailParams);
      
      if (result.success) {
        alert(`Quote sent successfully to ${quote.customerEmail}`);
        onClose();
      } else {
        setError(result.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending quote:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsSending(false);
    }
  };

  const handlePreview = () => {
    setIsPreview(!isPreview);
  };

  if (!isOpen || !quote) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name="Send" size={24} className="text-primary" />
            <h2 className="text-xl font-semibold text-text-primary">Send Quote to Customer</h2>
          </div>
          <button
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary transition-colors"
            disabled={isSending}
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* To Field (Read-only) */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Send to:
            </label>
            <Input
              value={quote.customerEmail}
              readOnly
              className="bg-background-secondary cursor-not-allowed"
            />
          </div>

          {/* Subject Field */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Subject:
            </label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              disabled={isSending}
            />
          </div>

          {/* Body Field */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-text-primary">
                Email Body:
              </label>
              <div className="text-xs text-text-secondary">
                Available variables: {'{CUSTOMER_NAME}'}, {'{COMPANY_NAME}'}, {'{QUOTE_REFERENCE}'}, {'{PROJECT_NAME}'}, {'{QUOTE_TOTAL}'}, {'{VALID_UNTIL_DATE}'}, {'{QUOTE_LINK}'}
              </div>
            </div>
            
            {isPreview ? (
              <div className="border border-border rounded-lg p-4 bg-background-secondary min-h-[300px] whitespace-pre-wrap">
                {replaceVariables(body)}
              </div>
            ) : (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Enter email body (use template variables like {CUSTOMER_NAME})"
                disabled={isSending}
                className="w-full border border-border rounded-lg p-3 min-h-[300px] text-sm text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-y"
              />
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            iconName={isPreview ? "Edit" : "Eye"}
            iconPosition="left"
            onClick={handlePreview}
            disabled={isSending}
          >
            {isPreview ? 'Edit' : 'Preview'}
          </Button>
          <Button
            variant="default"
            iconName={isSending ? "Loader" : "Send"}
            iconPosition="left"
            onClick={handleSend}
            disabled={isSending}
            className={isSending ? "opacity-75" : ""}
          >
            {isSending ? 'Sending...' : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendQuoteModal;